import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/admin/layout/Layout';
import type { Project, Device, User } from '../types';
import { adminApi } from '../api/admin';

const POLL_ONLINE_MS = 5000;

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [connectedDeviceIds, setConnectedDeviceIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOnlineIds = useCallback(async () => {
    try {
      const { deviceIds } = await adminApi.devices.getOnlineIds();
      setConnectedDeviceIds(new Set(Array.isArray(deviceIds) ? deviceIds : []));
    } catch {
      setConnectedDeviceIds(new Set());
    }
  }, []);

  useEffect(() => {
    document.title = 'Dashboard';
    return () => { document.title = 'VR Companion Admin'; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    Promise.all([
      adminApi.projects.list(),
      adminApi.devices.list(),
      adminApi.users.list(),
    ])
      .then(([projectsList, devicesList, usersList]) => {
        if (!cancelled) {
          setProjects(projectsList);
          setDevices(devicesList);
          setUsers(usersList);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.message || 'Failed to load dashboard data');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    fetchOnlineIds();
    const interval = setInterval(fetchOnlineIds, POLL_ONLINE_MS);
    return () => clearInterval(interval);
  }, [fetchOnlineIds]);

  const getEffectiveStatus = (deviceId: string) =>
    connectedDeviceIds.has(deviceId) ? 'online' : 'offline';

  const onlineCount = devices.filter((d) => getEffectiveStatus(d.id) === 'online').length;
  const recentProjects = projects.slice(-5).reverse();
  const recentDevices = devices.slice(-5).reverse();

  if (loading) {
    return (
      <Layout pageTitle="Dashboard">
        <div className="page-content">
          <p className="text-gray-400">Loading...</p>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout pageTitle="Dashboard">
        <div className="page-content">
          <p className="text-red-500">{error}</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout pageTitle="Dashboard">
      <div className="page-content">
        {/* Summary Cards */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-name">Total Projects</p>
                <p className="stat-value">{projects.length}</p>
              </div>
              <div className="stat-icon">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-name">Total Devices</p>
                <p className="stat-value">{devices.length}</p>
              </div>
              <div className="stat-icon">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-name">Online Devices</p>
                <p className="stat-value">{onlineCount}</p>
                <p className={`stat-change ${onlineCount > 0 ? 'stat-change-positive' : 'stat-change-negative'}`}>
                  {devices.length > 0 ? `${devices.length - onlineCount} offline` : '—'}
                </p>
              </div>
              <div className="stat-icon">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-name">Total Users</p>
                <p className="stat-value">{users.length}</p>
              </div>
              <div className="stat-icon">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Activity: Projects & Devices */}
        <div className="section-card">
          <div className="section-header">
            <h3 className="section-title">Recently created projects</h3>
          </div>
          {recentProjects.length === 0 ? (
            <p className="text-gray-500 text-sm">No projects created yet</p>
          ) : (
            <div className="divide-y divide-gray-200">
              {recentProjects.map((project) => (
                <div key={project.id} className="activity-item">
                  <div className="flex items-center justify-between">
                    <p className="activity-user">{project.name}</p>
                    <span className="text-sm text-gray-500">{project.description || '—'}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="section-card">
          <div className="section-header">
            <h3 className="section-title">Recently added devices</h3>
          </div>
          {recentDevices.length === 0 ? (
            <p className="text-gray-500 text-sm">No devices added yet</p>
          ) : (
            <div className="divide-y divide-gray-200">
              {recentDevices.map((device) => (
                <div key={device.id} className="activity-item">
                  <div className="flex items-center justify-between">
                    <p className="activity-user">{device.name}</p>
                    <span className="text-sm text-gray-500">{device.type} · {getEffectiveStatus(device.id)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="quick-actions">
          <h3 className="quick-actions-title">Quick Actions</h3>
          <div className="quick-actions-grid">
            <button type="button" className="quick-action-btn" onClick={() => navigate('/users')}>
              <svg className="w-5 h-5 mr-2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span className="quick-action-btn-text">Add New User</span>
            </button>
            <button type="button" className="quick-action-btn" onClick={() => navigate('/projects')}>
              <svg className="w-5 h-5 mr-2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span className="quick-action-btn-text">Create Project</span>
            </button>
            <button type="button" className="quick-action-btn" onClick={() => navigate('/devices')}>
              <svg className="w-5 h-5 mr-2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span className="quick-action-btn-text">Register Device</span>
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
